/**
 * Heading Component
 *
 * A semantic heading component (h1-h6) with tokenized typography.
 * Maps to the responsive type scale defined in CSS utilities.
 *
 * Features:
 * - Semantic HTML (h1-h6) based on level prop (preferred)
 * - Legacy 'as' prop support for backward compatibility
 * - Responsive typography via tokenized CSS classes
 * - Weight defaults optimized for each size
 * - Muted/colored variants
 * - Truncation and line-clamp support
 * - Text balance for better wrapping
 *
 * @example
 *```tsx
 * // NEW (preferred): Using level prop
 * <Heading level={1}>Page Title</Heading>
 * <Heading level={2} size="display-2">Featured Products</Heading>
 *
 * // LEGACY (backward compatible): Using as prop
 * <Heading as="h3"size="md">Section Title</Heading>
 *```
 *
 * @see typography.types.ts for type definitions
 * @see docs/tokens.md for token documentation
 */

import * as React from 'react';
import {cn} from '@/shared/utils/utils';
import type {HeadingLevel, HeadingProps, HeadingSize} from './typography.types';
import {AS_TO_LEVEL, headingVariants, LEVEL_TO_SIZE, lineClampClasses, SIZE_TO_WEIGHT} from './Heading.variants';

// =============================================================================
// HEADING COMPONENT
// =============================================================================

/**
 * Heading component with semantic HTML and tokenized typography.
 */
const Heading = React.memo(function Heading({
 level,
 as,
 size = 'auto',
 weight,
 variant = 'default',
 color,
 align = 'left',
 tracking,
 truncate = false,
 lineClamp,
 balance = false,
 mb,
 className,
 children,
 'data-testid': testId,
 ...props
}: HeadingProps): React.ReactElement {
 // Determine the semantic element
 // Priority: level > as > default (h2)
 let Tag: keyof React.JSX.IntrinsicElements;
 let effectiveLevel: HeadingLevel | undefined;

 if (level !== undefined) {
 Tag = `h${level}`;
 effectiveLevel = level;
 } else if (as !== undefined) {
 Tag = as;
 effectiveLevel = AS_TO_LEVEL[as];
 } else {
 Tag = 'h2';
 effectiveLevel = 2;
 }

  // Compute effective size from level when size="auto"
  // When size is explicitly provided (not 'auto'), use it directly
  // When size is 'auto', derive from level
  // IMPORTANT: Never pass 'auto' to CVA - it maps to empty string and won't apply size classes
  const computedSize = size !== 'auto'
    ? size
    : (effectiveLevel ? LEVEL_TO_SIZE[effectiveLevel] : 'display-2');

  // Ensure we never pass 'auto' to CVA - convert to concrete size
  // Type as Exclude to ensure TypeScript knows it's never 'auto' and CVA can recognize it
  const effectiveSize: Exclude<HeadingSize, 'auto'> = computedSize === 'auto'
    ? 'display-2'
    : (computedSize);

 // Compute effective weight (use provided or default for size)
 const effectiveWeight = weight ?? SIZE_TO_WEIGHT[effectiveSize] ?? 'semibold';

 // Build className - pass props directly to CVA
 const headingClassName = cn(
   headingVariants({
     size: effectiveSize,
     weight: effectiveWeight,
     align,
     truncate,
     balance,
     // Add variant or color (color takes precedence)
     ...(color ? { color } : { variant }),
     // Add optional props only if defined
     ...(tracking !== undefined ? { tracking } : {}),
     ...(mb !== undefined ? { mb } : {}),
   }),
   lineClamp && lineClampClasses[lineClamp],
   className
 );

 return (
 <Tag
 className={headingClassName}
 data-testid={testId}
 data-typography="heading"
 data-level={effectiveLevel}
 data-size={effectiveSize}
 {...props}
 >
 {children}
 </Tag>
 );
});

Heading.displayName = 'Heading';

// =============================================================================
// CONVENIENCE COMPONENTS (Legacy support)
// =============================================================================

/**
 * PageTitle - Main page title (h1)
 */
const PageTitle = React.memo(function PageTitle({
 children,
 className,
 ...props
}: Omit<HeadingProps, 'level' | 'as' | 'size'>): React.ReactElement {
 return (
 <Heading level={1} size="display" mb={4} className={className} {...props}>
 {children}
 </Heading>
 );
});

/**
 * SectionTitle - Section heading (h2)
 */
const SectionTitle = React.memo(function SectionTitle({
 children,
 className,
 ...props
}: Omit<HeadingProps, 'level' | 'as' | 'size'>): React.ReactElement {
 return (
 <Heading level={2} size="heading-1" mb={3} className={className} {...props}>
 {children}
 </Heading>
 );
});

/**
 * SubsectionTitle - Subsection heading (h3)
 */
const SubsectionTitle = React.memo(function SubsectionTitle({
 children,
 className,
 ...props
}: Omit<HeadingProps, 'level' | 'as' | 'size'>): React.ReactElement {
 return (
 <Heading level={3} size="heading-2" mb={2} className={className} {...props}>
 {children}
 </Heading>
 );
});

/**
 * CardHeading - Card/component heading (h4)
 */
const CardHeading = React.memo(function CardHeading({
 children,
 className,
 ...props
}: Omit<HeadingProps, 'level' | 'as' | 'size' | 'weight'>): React.ReactElement {
 return (
 <Heading level={4} size="lg" weight="semibold" className={className} {...props}>
 {children}
 </Heading>
 );
});

// =============================================================================
// EXPORTS
// =============================================================================

export {
  Heading,
  PageTitle,
  SectionTitle,
  SubsectionTitle,
  CardHeading,
};
// Export for tests
export { LEVEL_TO_SIZE };
